/**
 * Plain-language guide to Dridh Agent for front desk and back office staff.
 * Rendered by components/AgentGuide.js in the Agent tab.
 *
 * Every tool in TOOL_LABELS (agentContextMap.js) needs one entry in
 * ACTION_GROUPS; a test fails otherwise. Text may use **bold**.
 */

export const GUIDE_SECTIONS = [
  { id: 'start', title: 'Start here' },
  { id: 'how-to', title: 'How to use the agent' },
  { id: 'asking', title: 'How to ask' },
  { id: 'approvals', title: 'Approvals and undo' },
  { id: 'actions', title: 'What the agent can do' },
  { id: 'skills', title: 'Skills' },
  { id: 'integrations', title: 'Connected services' },
  { id: 'screen', title: 'A tour of the Agent tab' },
  { id: 'safety', title: 'Safety and privacy' },
  { id: 'help', title: 'When something goes wrong' },
  { id: 'limits', title: 'Limits' },
  { id: 'glossary', title: 'Words used in the agent' },
];

// ── Start here ──────────────────────────────────────────────────────────────

export const INTRO =
  'Dridh Agent is an assistant built into the PMS. Tell it what you need in plain words, the way you would ask a colleague. It looks things up, prepares the work and **asks you before it changes anything**.';

export const PROMISES = [
  { icon: 'approve', title: 'It asks first', text: 'Nothing in the PMS changes and no message is sent until you approve it.' },
  { icon: 'undo', title: 'Most changes can be undone', text: 'Check-ins, check-outs, payments, room blocks, stock and notes can be reversed in one click.' },
  { icon: 'record', title: 'Every step is recorded', text: 'What was asked, what the agent did and who approved it are kept in a record nobody can edit.' },
];

export const GOOD_AT = [
  'Answering questions about today, bookings, rooms, money and stock',
  'Repetitive desk work in one go: confirming, checking guests in and out, recording payments',
  'Blocking rooms and beds, and explaining why a room is not available',
  'Making CSV and PDF files and sending them by email or WhatsApp',
  'Following your team’s saved procedures (skills), like the morning brief',
];

export const WHO_CAN_USE =
  'Right now only **super admins** can use the agent. Other roles will get access later; this guide already explains how it works, including the rules that depend on your role.';

// ── How to use ──────────────────────────────────────────────────────────────

export const HOW_TO_STEPS = [
  {
    title: 'Open the agent',
    text: [
      'Go to the **Agent** tab, or press **Ask agent** at the bottom right of any PMS screen. The keyboard shortcut is **Ctrl + J** (**Cmd + J** on a Mac). **Esc** closes the side panel.',
      'When you open it from another screen, the agent knows which screen you are on and which booking is open, so you can say things like “check this guest in”.',
    ],
  },
  {
    title: 'Say what you need',
    text: ['Type your request and press **Enter** to send (**Shift + Enter** starts a new line). A new conversation shows suggestions you can click.'],
  },
  {
    title: 'Watch it work',
    text: [
      'Each thing the agent does appears as a step, such as “Checked arrivals” or “Found balances due”. Looking things up happens straight away and changes nothing.',
      'Press the square **Stop** button if you want it to stop.',
    ],
  },
  {
    title: 'Approve or reject the changes',
    text: [
      'Before anything changes, an **approval card** lists every change, one row each, with what will happen and any warnings.',
      'Untick the rows you do not want, then press **Approve all** (or **Approve 2 of 3**). **Reject** cancels the whole card. If you send a new message instead, the waiting changes are skipped.',
    ],
  },
  {
    title: 'Read the result',
    text: ['The agent tells you what happened, often with a table. A short summary shows how long it took and about how much desk work it covered.'],
  },
  {
    title: 'Undo if you need to',
    text: [
      'Press **Undo** on a row, **Undo these** for the whole card, or **Undo everything from this run**. You can also undo from the **Activity** tab.',
      'Emails and WhatsApp messages cannot be undone.',
    ],
  },
  {
    title: 'Keep your work tidy',
    text: [
      'Each chat is a **conversation** and is saved, so you can come back to it. Press **New conversation** for an unrelated task.',
      'Use the archive button to clear old conversations from the list; tick **Show archived** to see them again.',
    ],
  },
];

// ── How to ask ──────────────────────────────────────────────────────────────

export const ASKING_TIPS = [
  'Name the date or period: “today”, “tomorrow”, “10 to 12 October”, “last month”.',
  'Use booking numbers (OH-258), guest names or room names (D104, Meera female sharing).',
  'Say how you want the answer: a table, a CSV, a PDF, an email or a WhatsApp.',
  'Ask for many at once. The agent puts them on one approval card.',
  'Ask why: “Why can’t OH-258 check out?” or “Why is D104 not free next week?”',
  'If the agent misunderstood, just tell it. There is no need to start again.',
];

export const ASKING_EXAMPLES = [
  { instead: 'Check out guests', better: 'Check out every guest leaving today whose bill is settled' },
  { instead: 'Block the room', better: 'Block room D104 from 10 to 12 October for maintenance' },
  { instead: 'Send the report', better: 'Export this week’s payments report as a PDF and email it to me' },
  { instead: 'Who hasn’t paid?', better: 'Which guests staying now still owe money? Show booking number and amount' },
];

// ── Approvals and undo ─────────────────────────────────────────────────────

export const APPROVAL_TOPICS = [
  {
    title: 'What needs your approval',
    text: 'Anything that changes the PMS or leaves the building: confirming bookings, check-ins and check-outs, notes, payments, room blocks, stock, emails, WhatsApp messages and saving skills. Looking things up, calculating totals and making files do not need approval.',
  },
  {
    title: 'Reading an approval card',
    text: 'Each row is one change: one booking, one email, one block. Warnings point out things like money still owed. For messages you see who receives them and which files are attached, with a warning when a file holds guest personal data.',
  },
  {
    title: '“Not proposed” items',
    text: 'The agent checks every item before asking you. Items that cannot be done right now are listed under **not proposed**, each with the reason and a suggestion, for example “Record the payment first”.',
  },
  {
    title: 'If an approved change fails',
    text: 'The row shows **Why** it failed, based on what is true right now, and what to try instead. Reply “yes, do that” and the agent prepares a corrected card.',
  },
  {
    title: 'How undo works',
    text: 'Undo puts things back as they were before the agent acted: a checked-out guest goes back to checked in, a payment is removed, a block is deleted, stock is corrected. It only works while the record still looks the way the agent left it. You can undo your own agent actions; administrators can also undo other staff members’ actions from the **Activity** tab.',
  },
  {
    title: 'When undo is refused',
    text: 'If someone changed the record in the meantime (for example a colleague edited the booking), undo is refused and tells you what changed. **Force undo** on the approval card reverses the change anyway. Use it only when you have checked the record and are sure.',
  },
  {
    title: 'What can never be undone',
    text: 'Emails and WhatsApp messages that were sent, a payment that already has a refund, a stock change that would take stock below zero, and an undo itself.',
  },
];

// ── Actions ─────────────────────────────────────────────────────────────────
// approval: true when the action waits for approval.
// undo: 'yes' | 'no' for actions that change something; null when nothing in the PMS changes.

export const ACTION_GROUPS = [
  {
    id: 'front-desk',
    title: 'Front desk questions',
    intro: 'These look things up and change nothing, so they run straight away.',
    actions: [
      {
        tool: 'get_today_overview',
        title: 'Today at the front desk',
        what: 'A quick picture of one day: who is arriving, leaving and staying, who should have checked out already, bookings still waiting for confirmation, and money still owed.',
        examples: ['What’s happening today?', 'Give me tomorrow’s overview'],
        notes: ['Works for any date, not just today.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'search_bookings',
        title: 'Find bookings',
        what: 'Finds bookings by guest name, email, phone, booking number, status, payment status, where the booking came from, or dates. Each result shows the total and what is still owed.',
        examples: ['Find Priya Sharma’s booking', 'Unpaid bookings arriving between 1 and 7 October'],
        notes: ['Date ranges include both the first and the last day.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'get_booking_folio',
        title: 'Open a guest’s bill (folio)',
        what: 'The full bill of one booking: guest contact details, every charge (rooms, beds, courses, services), adjustments, payments, refunds, what is left to pay, web check-in status and the payment link.',
        examples: ['Show the folio for OH-258'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_arrivals',
        title: 'Are arrivals ready?',
        what: 'Guests arriving on a day and whether each one is ready: booking confirmed, web check-in done and bill paid. Anything missing is listed.',
        examples: ['Are today’s arrivals ready?', 'Who arrives tomorrow and hasn’t done web check-in?'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_checkout_candidates',
        title: 'Who can check out',
        what: 'Guests due to leave on a day, plus anyone who should have left already. Each one is marked ready or blocked, with the reason, such as money still owed or leaving before the booked date.',
        examples: ['Who is due to check out today, and who is blocked?'],
        notes: ['The agent uses this before checking guests out.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_outstanding_balances',
        title: 'Who still owes money',
        what: 'Bookings with money still to pay, with the guest’s contact details, the charges and their payment link. Look at guests staying now (the default), leaving today, arriving today, arriving in the next 7 days, or all active bookings.',
        examples: ['Which in-house guests still owe money?', 'Who arriving this week has a balance due?'],
        approval: false,
        undo: null,
      },
      {
        tool: 'get_payment_link',
        title: 'Get a payment link',
        what: 'The link a guest uses to pay what they owe. It opens the web check-in page, where the pending balance can be paid online.',
        examples: ['Get the payment link for OH-312'],
        approval: false,
        undo: null,
      },
    ],
  },
  {
    id: 'rooms-lookup',
    title: 'Rooms, stock and prices',
    intro: 'Also look-ups only: they change nothing.',
    actions: [
      {
        tool: 'check_availability',
        title: 'Check availability',
        what: 'Which rooms and beds are free for a stay, using the same rules as the booking website (check-in 3 pm, check-out 11 am).',
        examples: ['Which rooms are free from 12 to 15 October?', 'Is a female dorm bed free this weekend?'],
        notes: ['It says what is free, not why something is taken. For that, ask about the room calendar.', 'For female-only dorm beds, mention that the guest is female.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'get_room_calendar',
        title: 'Room calendar, night by night',
        what: 'For each room or bed and each night: free, blocked (and by which block) or booked (and by which booking), plus the free date windows.',
        examples: ['Why are the female dorm beds unavailable next week?', 'When is D104 next free for three nights?'],
        notes: ['A night is named by its date: “the night of 5 October” means the guest leaves on the morning of 6 October.', 'Shared rooms are shown bed by bed.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_availability_blocks',
        title: 'See room blocks',
        what: 'Existing blocks on rooms and beds (maintenance, deep cleaning, closures) with their dates, the nights they close and who created them.',
        examples: ['Which rooms are blocked this month?'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_inventory_items',
        title: 'Stock and items',
        what: 'Inventory items with their service, GST rate, quantity, unit price, stock value and the date they were added. It can show only items added in a period, for one service, or only low stock.',
        examples: ['Which items are below minimum stock?', 'Items added last month'],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_services_catalog',
        title: 'Services, courses and room types',
        what: 'The price list: add-on services (price and GST), upcoming courses (fee, dates and GST) and room types (rates, shared or private, number of rooms).',
        examples: ['Show the services catalogue with GST rates', 'Which courses start in the next 30 days?'],
        approval: false,
        undo: null,
      },
    ],
  },
  {
    id: 'reports',
    title: 'Reports, numbers and history',
    intro: 'Look-ups and calculations. Nothing changes.',
    actions: [
      {
        tool: 'get_report',
        title: 'Run a PMS report',
        what: 'Runs a report from the Reports tab for the dates you give, with the same numbers you would see there: master booking, occupancy, course participants, accounts, accounts master, payments, check-in, check-out, daily summary, services and inventory, and OMG experience.',
        examples: ['Payments report for this week', 'Summarise occupancy for this month'],
        approval: false,
        undo: null,
      },
      {
        tool: 'aggregate_rows',
        title: 'Calculate totals',
        what: 'Adds up, groups and averages numbers from an earlier result, for example GST per service or payments by method. The agent uses this for every total, so the maths is exact, not guessed.',
        examples: ['Group this week’s payments by method and total them'],
        approval: false,
        undo: null,
      },
      {
        tool: 'query_database',
        title: 'Answer other questions from PMS data',
        what: 'When no other action fits, the agent can look records up directly: list them, count them or add them up. It can only read. Passwords and identity documents are never shown.',
        examples: ['How many bookings came from the website in August?'],
        notes: ['A list shows at most 200 records at a time.'],
        approval: false,
        undo: null,
      },
      {
        tool: 'get_agent_action_history',
        title: 'What the agent did for you',
        what: 'Your recent agent actions, newest first, with whether each one worked and whether it can still be undone.',
        examples: ['What did you change for me today?'],
        approval: false,
        undo: null,
      },
    ],
  },
  {
    id: 'bookings-change',
    title: 'Changing bookings',
    intro: 'These change bookings, so each one waits for your approval. All of them can be undone.',
    actions: [
      {
        tool: 'confirm_bookings',
        title: 'Confirm bookings',
        what: 'Moves bookings from **Pending** to **Confirmed**.',
        examples: ['Confirm today’s pending arrivals'],
        notes: ['Only pending bookings can be confirmed; the others are listed as not proposed.', 'You see a warning when no payment has been recorded yet.', 'Undo puts the booking back to Pending.'],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'check_in_bookings',
        title: 'Check in guests',
        what: 'Moves confirmed bookings to **Checked in**.',
        examples: ['Check in Rahul Mehta and Anna Weber, they are at the desk'],
        notes: [
          'The booking must be confirmed first. The agent can confirm it in the same conversation.',
          'Staff can check in on the arrival day or the day after. Property admins and super admins can check in at any time.',
          'Warnings show when money is owed or web check-in is not done. You can still approve.',
          'Undo puts the booking back to Confirmed.',
        ],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'check_out_bookings',
        title: 'Check out guests',
        what: 'Moves checked-in bookings to **Checked out**.',
        examples: ['Check out every guest whose folio is settled'],
        notes: [
          'Blocked when the bill is not fully paid or the stay has not ended. A super admin can still approve these, and the card shows a warning.',
          'Warns when a guest has paid too much and a refund may be due.',
          'Undo puts the booking back to Checked in.',
        ],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'update_booking_notes',
        title: 'Add a note to a booking',
        what: 'Adds a note to a booking, stamped with the date and your name. It replaces all existing notes only if you ask for that.',
        examples: ['Add a note to OH-258: guest asked for a late check-out at 1 pm'],
        notes: ['Undo restores the notes as they were.'],
        approval: true,
        undo: 'yes',
      },
    ],
  },
  {
    id: 'money-rooms-stock',
    title: 'Money, room blocks and stock',
    intro: 'Changes that wait for your approval and can be undone.',
    actions: [
      {
        tool: 'record_payment',
        title: 'Record a payment',
        what: 'Writes down money the property has **already received** (cash, UPI, card, bank transfer, Razorpay or other) on a guest’s bill.',
        examples: ['Record ₹4,500 cash for OH-258'],
        notes: [
          'It does not charge the guest. It only records a payment you already have.',
          'The amount cannot be more than what the guest still owes.',
          'Undo removes the payment and restores the payment status, unless the payment was changed or refunded since.',
        ],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'create_availability_blocks',
        title: 'Block rooms or beds',
        what: 'Closes rooms or beds for dates so they cannot be booked, for maintenance, deep cleaning or closures. Blocking a shared room blocks all of its beds.',
        examples: ['Block room D104 for maintenance tomorrow', 'Block Meera female sharing from 10 to 12 October for deep cleaning'],
        notes: [
          'Before asking you, it checks every night for bookings and existing blocks. It proposes only the nights that are still free and tells you what holds the others.',
          'A block from 10 to 12 October closes the nights of 10 and 11 October; the room can be booked again for the night of the 12th.',
          'Undo removes the blocks it created.',
        ],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'adjust_inventory',
        title: 'Adjust stock',
        what: 'Adds or removes units of inventory items, with a reason.',
        examples: ['Restock white robes by 20 units', 'Remove 2 bath towels, damaged'],
        notes: ['Stock can never go below zero.', 'Undo reverses the change if nobody has changed that item’s quantity since.'],
        approval: true,
        undo: 'yes',
      },
    ],
  },
  {
    id: 'files-messages',
    title: 'Files, email and WhatsApp',
    intro: 'Making a file runs straight away. Sending anything waits for your approval and **cannot be recalled**.',
    actions: [
      {
        tool: 'export_data_file',
        title: 'Make a CSV or PDF file',
        what: 'Turns a list or report from the conversation into a file: **CSV** to open in Excel or Google Sheets, **PDF** to read or print. The file stays with the conversation and can be attached to an email or WhatsApp.',
        examples: ['Export in-house guests with a balance as a CSV', 'Make a PDF of today’s arrivals'],
        notes: [
          'Making a file changes nothing in the PMS, so it does not ask for approval. Sending the file does.',
          'Files with guest names, phone numbers, emails or ID details are marked **Personal data**.',
          'Up to 5,000 rows in a CSV and 2,000 rows in a PDF, 10 MB per file.',
        ],
        approval: false,
        undo: null,
      },
      {
        tool: 'list_exported_files',
        title: 'Find files you made',
        what: 'Files made in this conversation, or your recent files from any conversation.',
        examples: ['Show the files I exported this week'],
        approval: false,
        undo: null,
      },
      {
        tool: 'send_email',
        title: 'Send an email',
        what: 'Sends an email to anyone (colleagues, owners, vendors or guests) through the property’s email service, with files the agent made attached if you like.',
        examples: ['Email me a CSV of this week’s payments', 'Email the arrivals PDF to the housekeeping team'],
        notes: [
          'Each email is its own row on the approval card, so you can approve some and not others.',
          'Up to 20 recipients plus 20 in copy per email, and up to 5 attachments (15 MB in total).',
          'The card warns you when an attachment holds guest personal data.',
          'Sent emails cannot be recalled.',
        ],
        approval: true,
        undo: 'no',
      },
      {
        tool: 'send_whatsapp',
        title: 'Send a WhatsApp message',
        what: 'Sends WhatsApp messages from the property’s WhatsApp account: a normal text, an approved template, or a file with a caption. Give a phone number or a booking.',
        examples: ['WhatsApp today’s arrivals PDF to 919812345678'],
        notes: [
          '**Templates** reach anyone at any time.',
          '**Normal text and files** reach only people who messaged the property in the last 24 hours. If they have not, send a template or use email.',
          'Numbers need the country code (91 for India).',
          'Up to 30 messages at once. Sent messages cannot be recalled.',
        ],
        approval: true,
        undo: 'no',
      },
      {
        tool: 'list_whatsapp_templates',
        title: 'See WhatsApp templates',
        what: 'The approved WhatsApp message templates and the details each one needs filled in.',
        examples: ['Which WhatsApp templates can we use?'],
        approval: false,
        undo: null,
      },
      {
        tool: 'send_guest_messages',
        title: 'Message many guests at once',
        what: 'Sends the same message to the guests of several bookings, by email (the default) or WhatsApp template, for example payment reminders. Each guest’s own details are filled in.',
        examples: ['Remind in-house guests who still owe money, with a payment link'],
        notes: [
          'Details filled in for each guest: name, booking number, balance due, payment link, check-in and check-out dates.',
          'Guests without an email address (or a phone number, for WhatsApp) are skipped and listed.',
          'WhatsApp reminders use the template an administrator set in Agent Settings.',
          'Up to 50 bookings at once. Sent messages cannot be recalled.',
        ],
        approval: true,
        undo: 'no',
      },
    ],
  },
  {
    id: 'agent',
    title: 'Skills and undo by asking',
    intro: 'Actions about the agent itself.',
    actions: [
      {
        tool: 'list_skills',
        title: 'List skills',
        what: 'The saved procedures the agent knows, with what each one is for.',
        examples: ['What skills do you have?'],
        approval: false,
        undo: null,
      },
      {
        tool: 'load_skill',
        title: 'Open a skill',
        what: 'Reads the full steps of a skill before following them. You see “Loaded skill” when this happens.',
        examples: ['Use the morning front desk brief skill'],
        approval: false,
        undo: null,
      },
      {
        tool: 'save_skill',
        title: 'Save a skill',
        what: 'Saves a way of working as a skill so it can be reused, or saves a new version of an existing skill.',
        examples: ['Save what we just did as a skill called “Weekly balance report”'],
        notes: ['Undo archives a new skill, or brings back the previous version of an existing one.'],
        approval: true,
        undo: 'yes',
      },
      {
        tool: 'undo_agent_actions',
        title: 'Undo actions by asking',
        what: 'Instead of pressing Undo, you can ask in words. The agent finds the actions and proposes the undo for your approval.',
        examples: ['Undo the check-outs you did this morning'],
        notes: ['It refuses when a record changed since, unless you tell it to force the undo.', 'An undo cannot itself be undone. To go back, ask the agent to do the action again.'],
        approval: true,
        undo: 'no',
      },
    ],
  },
];

export const documentedToolNames = () => ACTION_GROUPS.flatMap((group) => group.actions.map((action) => action.tool));

// ── Skills ──────────────────────────────────────────────────────────────────

export const SKILLS_INTRO = [
  'A **skill** is a saved, step-by-step way of doing a task, like a checklist or standard procedure. When your request matches a skill, the agent follows its steps, so the work is done the same way every time.',
  'You do not need to name a skill. Ask as usual (“Are today’s arrivals ready?”) and the agent picks the right one. Skills never skip approvals.',
];

export const BUILTIN_SKILL_GUIDES = [
  {
    slug: 'morning-front-desk-brief',
    name: 'Morning front desk brief',
    what: 'A one-screen start to the day: arrivals, departures, overdue check-outs, money owed and low stock, with up to three suggested next steps.',
    changes: 'Changes nothing.',
    example: 'Give me the morning front desk brief',
  },
  {
    slug: 'checkout-settlement-sweep',
    name: 'Check-out settlement sweep',
    what: 'Finds everyone due to leave, including anyone overdue, checks out every guest whose bill is settled on one approval card, and lists who still owes money.',
    changes: 'Check-outs wait for your approval. For guests who owe money it offers to send a reminder or record a payment you already received.',
    example: 'Check out every guest whose folio is settled',
  },
  {
    slug: 'arrivals-readiness-check',
    name: 'Arrivals readiness check',
    what: 'Checks today’s arrivals for confirmation, web check-in and payment, and shows who is ready and what is missing.',
    changes: 'It then offers to confirm bookings, message guests or check in those at the desk, and does nothing until you choose.',
    example: 'Are today’s arrivals ready?',
  },
  {
    slug: 'pending-payment-reminders',
    name: 'Pending payment reminders',
    what: 'Finds guests who owe money (guests staying now, unless you say otherwise) and sends each one a short, polite reminder with their payment link.',
    changes: 'All reminders are on one approval card. Email unless you ask for WhatsApp. Sent reminders cannot be recalled.',
    example: 'Remind in-house guests who still owe money, with a payment link',
  },
  {
    slug: 'monthly-inventory-gst-report',
    name: 'Monthly inventory GST report',
    what: 'Items added in a month (last month unless you say otherwise), grouped by service, with stock value and GST per service and in total, and any low-stock items.',
    changes: 'Changes nothing. Every figure comes from the calculator, not from guessing.',
    example: 'Classify inventory added last month by service and total the GST',
  },
  {
    slug: 'export-and-share-report',
    name: 'Export and share a report',
    what: 'Builds a list or report, turns it into a CSV or PDF, and sends it by email or WhatsApp.',
    changes: 'Sending waits for your approval. If you do not name anyone, you get the file to download and the agent asks whether to send it.',
    example: 'Export last week’s payments report as a PDF and email it to me',
  },
];

export const SKILL_USE_STEPS = [
  'Open **Agent → Skills** and pick a skill to read its steps.',
  'Press **Use in chat** to start a conversation with it, or simply ask for the task in a conversation.',
  'Approve the changes as usual.',
];

export const SKILL_EDITOR_FIELDS = [
  { field: 'Name', meaning: 'What people see, for example “Late check-out policy”.' },
  { field: 'Slug', meaning: 'A short id in lowercase words joined by hyphens, for example late-checkout-policy. It cannot be changed later.' },
  { field: 'Description', meaning: 'What the skill does, in one sentence.' },
  { field: 'When to use', meaning: 'The kind of request that should use it, for example “The user asks about late check-out”.' },
  { field: 'Instructions', meaning: 'Numbered steps in plain words, and what the answer should look like. **Preview** shows how it will look.' },
  { field: 'Tools', meaning: 'The actions the skill may use. Start typing to pick from the list.' },
  { field: 'Minutes by hand', meaning: 'How long the task takes a person. It counts towards “Desk time covered”.' },
];

export const SKILL_RULES = [
  'Press **New skill** in the Skills tab, or ask the agent: “Save what we just did as a skill”. The agent’s draft waits for your approval.',
  'Every save becomes a new version. Under **Earlier versions**, **Restore** brings an old version back.',
  '**Archive** hides a skill from the agent; tick **Archived** to see archived skills.',
  'Administrators can edit any skill. Everyone else can edit the skills they created. Built-in skills can only be edited by administrators.',
];

// ── Connected services ─────────────────────────────────────────────────────

export const INTEGRATIONS = [
  {
    id: 'pms',
    icon: 'database',
    title: 'Your PMS data',
    what: 'The agent works on the same records you see on the PMS screens.',
    points: [
      'Bookings, guest bills (folios), payments, the availability calendar, room blocks, stock, services, courses and reports.',
      'Changes go through the same checks as the PMS screens and follow your role’s permissions.',
      'Open screens update by themselves after an approved change or an undo: availability calendar, bookings, payment log, items, web check-ins and night audit, even in another browser tab.',
      'Every agent change is also written to the PMS audit log.',
    ],
  },
  {
    id: 'email',
    icon: 'mail',
    title: 'Email',
    what: 'Sends email through the property’s email service.',
    points: [
      'Send to anyone: colleagues, owners, vendors or guests.',
      'Attach CSV or PDF files the agent made: up to 5 files, 15 MB in total.',
      'Up to 20 recipients, plus 20 in copy, per email.',
      'Reminders to many guests fill in each guest’s name, booking number, balance and payment link.',
      'Every email waits for your approval, cannot be recalled once sent, and is recorded.',
    ],
  },
  {
    id: 'whatsapp',
    icon: 'message',
    title: 'WhatsApp (WATI)',
    what: 'Sends WhatsApp messages from the property’s WhatsApp account.',
    points: [
      '**Templates** (pre-approved messages) reach anyone at any time. Ask “Which WhatsApp templates can we use?” to see them.',
      '**Normal text and files** reach only people who messaged the property in the last 24 hours. Otherwise the send fails: use a template or email instead.',
      'Give numbers with the country code (91 for India), or name the booking and the agent uses the guest’s number.',
      'WhatsApp payment reminders use the template an administrator chose in Agent Settings.',
      'Every message waits for your approval and cannot be recalled once sent.',
    ],
  },
  {
    id: 'files',
    icon: 'file',
    title: 'CSV and PDF files',
    what: 'Lists and reports from a conversation, saved as files.',
    points: [
      '**CSV** opens in Excel or Google Sheets. **PDF** is for reading and printing and can show totals under the title.',
      'Download from the file card in the conversation, or from **Files** in the conversation’s side panel.',
      'Files marked **Personal data** contain guest names, phone numbers, emails or ID details.',
      'Only the person who made a file, and administrators, can download it.',
      'Up to 5,000 rows in a CSV, 2,000 rows in a PDF, 10 MB per file.',
    ],
  },
  {
    id: 'payment-links',
    icon: 'link',
    title: 'Guest payment links',
    what: 'Each booking has a link where the guest can pay what is still owed.',
    points: [
      'The link opens the web check-in page, where the pending balance is paid online.',
      'The agent adds it to payment reminders automatically.',
      'Ask “Get the payment link for OH-312” to get one yourself.',
      'An administrator sets the link format in Agent Settings.',
    ],
  },
  {
    id: 'reports',
    icon: 'chart',
    title: 'PMS reports',
    what: 'The same reports as the Reports tab, for the dates you give.',
    points: [
      'Master booking, occupancy, course participants, accounts, accounts master, payments, check-in, check-out, daily summary, services and inventory, and OMG experience.',
      'Ask for totals, a CSV or a PDF of any of them.',
    ],
  },
  {
    id: 'ai',
    icon: 'bot',
    title: 'AI models',
    what: 'The agent’s understanding and writing come from an AI service.',
    points: [
      'Supported: Anthropic (Claude), OpenAI, Google Gemini, Meta Muse Spark and Groq.',
      'An administrator adds them in **Agent → Settings** with the provider’s key and can press **Test connection** to check them. Keys are stored encrypted and never shown again.',
      'If more than one is set up, pick which one answers from the menu above the chat.',
      'The details the agent looks up are sent to that AI service so it can write the answer.',
    ],
  },
  {
    id: 'guest-assistant',
    icon: 'globe',
    title: 'Guest assistant (not live yet)',
    what: 'A separate assistant for guest questions.',
    points: [
      'It can answer questions about availability, room types, courses and add-ons.',
      'It cannot see guest details or change anything.',
      'It is built but switched off and is not on the website yet.',
    ],
  },
];

// ── Tour ────────────────────────────────────────────────────────────────────

export const SCREEN_TOUR = [
  {
    title: 'Numbers at the top',
    points: [
      '**Desk time covered**: about how long the same work would take by hand.',
      '**Agent runs**: how many requests the agent handled.',
      '**Actions executed** and **Undone**: approved changes, and how many were reversed.',
      'All numbers cover the last 30 days.',
    ],
  },
  {
    title: 'Conversations',
    points: [
      'The list on the left shows your conversations. A dot shows **Working**, **Needs approval** or **Error**; “2 to approve” means changes are waiting for you.',
      'The chat is in the middle. The menu above it chooses which AI agent answers. “No agent configured” means an administrator still has to add one.',
    ],
  },
  {
    title: 'The side panel of a conversation',
    points: [
      '**Actions**: every change proposed in the conversation, its status, and Undo.',
      '**Files**: files the agent made, ready to download.',
      '**Ledger**: every step, in order. **Verify chain** checks that nobody has altered the record.',
      '**Context**: what the agent knows about the screen you asked from.',
    ],
  },
  {
    title: 'Skills, Activity and Guide',
    points: [
      '**Skills**: read, use, create, edit and restore skills.',
      '**Activity**: every agent action with who, what and when. Filter the list and undo from here.',
      '**Guide**: this page.',
    ],
  },
  {
    title: 'Settings (administrators)',
    points: ['Add AI agents and their keys, test them, choose the default, and set the payment link and the WhatsApp reminder template.'],
  },
  {
    title: 'On other PMS screens',
    points: [
      'Press **Ask agent** at the bottom right. The side panel suggests requests that fit that screen.',
      '**Open in the Agent tab** moves the conversation to the full view, where it continues.',
    ],
  },
];

// ── Safety ──────────────────────────────────────────────────────────────────

export const CANNOT_DO = ['Delete or cancel bookings', 'Give refunds', 'Change room prices or rates', 'Create users or change roles', 'Pause the PMS'];

export const SAFETY_POINTS = [
  { title: 'Nothing changes without you', text: 'Every change and every message waits on an approval card until you approve it.' },
  { title: 'It works as you', text: 'The agent can only do what your role allows, and the PMS checks each change again.' },
  { title: 'Read every card before approving', text: 'Guest notes and messages are written by guests. If such text tries to tell the agent to do something, the approval card is your safety check: approve only rows you asked for.' },
  { title: 'Guest details and AI', text: 'To answer, the agent sends what it looked up, which can include guest names and contact details, to the AI service your administrator chose. Ask only for what you need.' },
  { title: 'Messages leave the building', text: 'Emails and WhatsApp messages cannot be recalled. Check recipients and attachments before approving.' },
  { title: 'Files with personal data', text: 'Share files marked **Personal data** only with people who need them. Only you and administrators can download your files.' },
  { title: 'Everything is recorded', text: 'Every step is kept in the Ledger, which cannot be edited, and agent changes appear in the PMS audit log.' },
];

// ── Help ────────────────────────────────────────────────────────────────────

export const TROUBLESHOOTING = [
  {
    question: 'The message box says “Ask an administrator to add an agent”',
    answer: 'No AI agent has been set up yet. An administrator adds one in Agent → Settings.',
  },
  {
    question: 'Some items are listed under “not proposed”',
    answer: 'The agent checked them and they cannot be done right now. Each one shows the reason and a suggestion. Ask the agent to follow the suggestion, for example “record the payment first, then check out”.',
  },
  {
    question: 'An item I approved failed',
    answer: 'Open **Why** on that row: it explains what is true now and what to try. Reply “yes, try that” and the agent prepares a corrected approval card.',
  },
  {
    question: 'Undo was refused',
    answer: 'Someone or something changed the record after the agent acted. Check the record first. If you are sure, press **Force undo**. Sent messages, refunded payments and stock that would go below zero can never be undone.',
  },
  {
    question: 'A WhatsApp message did not go through',
    answer: 'Normal text and files only reach people who messaged the property in the last 24 hours. Send an approved template instead, or use email.',
  },
  {
    question: 'My approval card disappeared after I sent a message',
    answer: 'Sending a new message skips the changes that were waiting. Ask again and a new card appears.',
  },
  {
    question: 'It is taking a long time',
    answer: 'Press **Stop**. Each request can take only a limited number of steps, so split a big task into smaller requests.',
  },
  {
    question: 'It says there are too many requests',
    answer: 'You can send up to 20 messages a minute. Wait a minute and try again.',
  },
  {
    question: 'A number looks wrong',
    answer: 'Ask “how did you calculate that?” and check the dates it used. Totals come from the calculator over the records it read, and reports match the Reports tab for the same dates.',
  },
  {
    question: 'My screen did not update after a change',
    answer: 'Most screens update by themselves within a few seconds. If one does not, refresh the page.',
  },
  {
    question: 'It stopped in the middle of a task',
    answer: 'If the PMS restarts, work in progress stops, but approval cards waiting for you are kept. Ask again to continue.',
  },
];

export const LIMITS = [
  { what: 'Bookings on one card (confirm, check in, check out, guest messages)', limit: '50' },
  { what: 'Room blocks in one request', limit: '30, each up to 366 nights' },
  { what: 'Stock adjustments in one request', limit: '50' },
  { what: 'Emails in one request', limit: '20' },
  { what: 'Recipients per email', limit: '20, plus 20 in copy' },
  { what: 'Attachments per email', limit: '5 files, 15 MB in total' },
  { what: 'WhatsApp messages in one request', limit: '30' },
  { what: 'CSV file', limit: '5,000 rows, 10 MB' },
  { what: 'PDF file', limit: '2,000 rows, 10 MB' },
  { what: 'Records in one data look-up', limit: '200' },
  { what: 'Booking note', limit: '2,000 characters' },
  { what: 'Messages to the agent', limit: '20 per minute' },
  { what: 'Steps per request', limit: 'Set by an administrator (25 unless changed)' },
];

export const GLOSSARY = [
  { term: 'Conversation', meaning: 'One chat with the agent. It is saved so you can come back to it.' },
  { term: 'Run', meaning: 'Everything the agent does in answer to one of your messages.' },
  { term: 'Step', meaning: 'One thing the agent did during a run, such as “Checked arrivals”.' },
  { term: 'Approval card', meaning: 'The list of changes waiting for you to approve or reject.' },
  { term: 'Not proposed', meaning: 'Items the agent checked and left out, with the reason.' },
  { term: 'Undo', meaning: 'Reverses a change the agent made, while the record is unchanged since.' },
  { term: 'Force undo', meaning: 'Undo even though the record changed since. Use with care.' },
  { term: 'Skill', meaning: 'A saved step-by-step procedure the agent follows.' },
  { term: 'Folio', meaning: 'A guest’s bill: charges, payments, refunds and balance.' },
  { term: 'Balance due', meaning: 'Money the guest still owes.' },
  { term: 'Block', meaning: 'Dates when a room or bed is closed for booking, for example for maintenance.' },
  { term: 'WhatsApp template', meaning: 'A pre-approved message format that can be sent to anyone at any time.' },
  { term: '24-hour window', meaning: 'After a person messages the property on WhatsApp, normal text and files can be sent to them for 24 hours.' },
  { term: 'Personal data', meaning: 'Guest names, phone numbers, emails and ID details.' },
  { term: 'Ledger', meaning: 'The permanent record of every step in a conversation. It cannot be edited.' },
  { term: 'Context', meaning: 'What the agent knows about the screen you asked from, such as the open booking.' },
  { term: 'Desk time covered', meaning: 'An estimate of how long the same work would take by hand.' },
];
